/**
 * Template Modifier Utilities
 *
 * Provides pure functions for transforming QR templates based on preview type.
 * The primary use case is applying center label and logo display rules
 * for different QR code types (site, fixed bay, super bay).
 *
 * Display Rules:
 * - Site QR: No center label, logo if configured
 * - Super Bay QR: No center label, logo if configured
 * - Fixed Bay QR: Center label if enabled, logo only if center disabled
 *
 * Requirements: 3.1, 3.2, 3.3, 4.1, 4.2, 4.3, 4.4, 4.5
 */

import type { QrTemplate } from '../types';

/**
 * Preview configuration for a specific QR code type.
 * Used to determine which display rules to apply.
 */
export interface PreviewConfig {
  /** The type of QR code being previewed */
  readonly type: 'site' | 'fixed-bay' | 'super-bay';
  /** Bay identifier (for fixed-bay and super-bay types) */
  readonly bayId?: string;
  /** Bay number/label (for fixed-bay type) */
  readonly bayNumber?: string;
  /** Bay name (for super-bay type) */
  readonly bayName?: string;
  /** Site name (for corner label on site QR codes) */
  readonly siteName?: string;
}

/**
 * Effective template after applying type-specific display rules.
 * Extends QrTemplate with computed display flags.
 */
export interface EffectiveTemplate extends QrTemplate {
  /** Whether to show the center label (bay number) */
  readonly showCenterLabel: boolean;
  /** Whether to show the logo overlay */
  readonly showLogo: boolean;
  /** Text to display in the center label (bay number as string) */
  readonly centerLabelText?: string;
  /** Text to display in the corner label (bay ID/name or site name) */
  readonly cornerLabelText?: string;
}

/**
 * Computes the effective template for a given preview type.
 *
 * This pure function transforms a base QR template by applying
 * type-specific display rules for center labels and logos.
 *
 * Rules:
 * - Site QR: No center label, logo if configured and center enabled
 * - Super Bay QR: No center label, logo if configured and center enabled
 * - Fixed Bay QR: Center label if enabled AND showBayLabel is true,
 *   logo if configured and center enabled (logo shows when showBayLabel is false)
 *
 * Corner Label Rules (when showCornerLabel is enabled):
 * - Site QR: Shows site name
 * - Fixed Bay QR: Shows "Bay {bayNumber}"
 * - Super Bay QR: Shows bay name, falls back to bay ID if no name
 *
 * @param baseTemplate - The base QR template with customization settings
 * @param previewConfig - Configuration specifying the QR code type and bay info
 * @returns Effective template with computed display flags
 *
 * @example
 * // Site QR with logo
 * getEffectiveTemplate(
 *   { ...template, logoDataUrl: 'data:image/png;...' },
 *   { type: 'site' }
 * )
 * // Returns: { ...template, showCenterLabel: false, showLogo: true }
 *
 * @example
 * // Fixed bay QR with center enabled and showBayLabel true
 * getEffectiveTemplate(
 *   { ...template, centerEnabled: true, showBayLabel: true, logoDataUrl: 'data:image/png;...' },
 *   { type: 'fixed-bay', bayId: 'bay-1', bayNumber: '1' }
 * )
 * // Returns: { ...template, showCenterLabel: true, showLogo: false, centerLabelText: '1' }
 *
 * @example
 * // Fixed bay QR with center enabled but showBayLabel false (logo instead)
 * getEffectiveTemplate(
 *   { ...template, centerEnabled: true, showBayLabel: false, logoDataUrl: 'data:image/png;...' },
 *   { type: 'fixed-bay', bayId: 'bay-1', bayNumber: '1' }
 * )
 * // Returns: { ...template, showCenterLabel: false, showLogo: true }
 *
 * Requirements: 3.1, 3.2, 3.3, 4.1, 4.2, 4.3, 4.4, 4.5
 */
export function getEffectiveTemplate(
  baseTemplate: QrTemplate,
  previewConfig: PreviewConfig
): EffectiveTemplate {
  const isFixedBay = previewConfig.type === 'fixed-bay';
  const centerEnabled = baseTemplate.centerEnabled;
  const hasLogo = baseTemplate.logoDataUrl !== null;
  const showBayLabel = baseTemplate.showBayLabel;

  // Center label rules:
  // - Only show on fixed bays when center customization is enabled AND showBayLabel is true
  // - Never show on site or super bay QR codes
  const showCenterLabel = isFixedBay && centerEnabled && showBayLabel;

  // Logo display rules:
  // - Site QR: Show logo if configured and center enabled
  // - Super Bay QR: Show logo if configured and center enabled
  // - Fixed Bay QR: Show logo if configured, center enabled, and showBayLabel is false
  const showLogo = hasLogo && centerEnabled && (!isFixedBay || !showBayLabel);

  // Center label text is the bay number (only for fixed bays)
  const centerLabelText = isFixedBay && previewConfig.bayNumber !== undefined
    ? previewConfig.bayNumber
    : undefined;

  // Corner label text based on QR type (when showCornerLabel is enabled)
  let cornerLabelText: string | undefined;
  if (baseTemplate.showCornerLabel) {
    switch (previewConfig.type) {
      case 'site':
        cornerLabelText = previewConfig.siteName;
        break;
      case 'fixed-bay':
        cornerLabelText = previewConfig.bayNumber !== undefined
          ? previewConfig.bayNumber
          : undefined;
        break;
      case 'super-bay':
        // Use bay name if available, otherwise fall back to bay ID
        cornerLabelText = previewConfig.bayName || previewConfig.bayId;
        break;
    }
  }

  return {
    ...baseTemplate,
    showCenterLabel,
    showLogo,
    centerLabelText,
    cornerLabelText,
  };
}
